import type {
  PersonSportOrganizationResponse,
  PlayerOrgResponse,
  PlayerProfileResponse,
  RpmPicks,
  RpmSummaryResponse,
} from '@on3/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { CompactHeader } from '@public/components/Db/Profile/Header/CompactHeader';
import { DatabaseLayout } from '@public/components/Layouts/DatabaseLayout';
import { Locked } from '@public/components/Svg/Locked';
import { Unlocked } from '@public/components/Svg/Unlocked';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import { format } from 'date-fns';

import { InsiderPredictions } from './InsiderPredictions';
import { PlayerRpmItem } from './PlayerRpmItem';
import styles from './PlayerRpmPage.module.scss';

export interface IPlayerRpmPageProps {
  rpmSummary?: RpmSummaryResponse;
  basicInfo?: PlayerProfileResponse;
  rpmPicks?: RpmPicks[];
  playerOrganizations?: PlayerOrgResponse;
  currentOrg?: PersonSportOrganizationResponse;
}

export const PlayerRpmPage = ({
  rpmSummary,
  basicInfo,
  rpmPicks,
  playerOrganizations,
}: IPlayerRpmPageProps) => {
  const isLocked = rpmSummary?.locked;
  const teamColor = (primaryColor?: string | null) => ({
    background: `${primaryColor || '#000000'}`,
  });
  const currentOrg =
    (playerOrganizations?.organizations?.length &&
      playerOrganizations?.organizations[0]) ||
    {};
  const year = currentOrg?.rating?.year;

  return (
    <DatabaseLayout>
      <section>
        {basicInfo && <CompactHeader basicInfo={basicInfo} />}
        <div className={styles.logoBlock}>
          <img
            alt="recruiting prediction machine"
            className={styles.rpmLogo}
            height="30"
            src="https://on3static.com/static/on3/rpm-logo.png"
            width="365"
          />
        </div>
        <div className={styles.rpmBlock}>
          <Text className={styles.description} paragraph variant="overline">
            <b>Rivals RPM</b>
          </Text>
          <div className={styles.status}>
            <Text
              className={clsx(styles.predictionStatus, {
                [styles.locked]: isLocked,
              })}
              variant="subtitle1"
            >
              {isLocked ? (
                <>
                  <Locked /> Predictions Locked
                </>
              ) : (
                <>
                  <Unlocked /> Predictions Unlocked
                </>
              )}
            </Text>
          </div>
          <ol className={styles.rpmList}>
            {rpmSummary?.predictions?.map((subRpm) => {
              const sluggedSport = slugify(
                basicInfo?.defaultSport?.name || 'football',
              );

              return (
                <li
                  className={styles.teamContainer}
                  key={`${subRpm?.organization?.key}-${subRpm.percent}`}
                >
                  {subRpm.committed && (
                    <Text
                      className={styles.rpmCommitTag}
                      style={teamColor(subRpm.organization?.primaryColor)}
                      variant="subtitle1"
                    >
                      Committed -{' '}
                      {subRpm.committedDate &&
                        format(new Date(subRpm.committedDate), 'MM/dd/yy')}
                    </Text>
                  )}
                  {year && (
                    <PlayerRpmItem
                      sport={sluggedSport}
                      year={year}
                      {...subRpm}
                    />
                  )}
                </li>
              );
            })}
          </ol>

          <Text
            className={styles.predictionData}
            component="h6"
            variant="overline"
          >
            Prediction Data
          </Text>
          <div className={styles.factorsBlock}>
            <Text className={styles.factorsList}>
              Rivals Recruiting Insiders <span>/</span> Visit Center{' '}
              <span>/</span> Fan Site Insiders <span>/</span> Industry Leaders{' '}
              <span>/</span> Social Sentiment
            </Text>
            <Text className={styles.factorsList}>
              Media Sentiment <span>/</span> Geographic Data <span>/</span>{' '}
              Coaching Staff Success <span>/</span> State and High School
              Historical Data
            </Text>
          </div>
        </div>

        <InsiderPredictions basicInfo={basicInfo} picks={rpmPicks} />
      </section>
    </DatabaseLayout>
  );
};
